import { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import './index.scss';
import Button from '../global/Button';
import TextInput from '../global/Inputs/Text';
import { submitPdfRequest } from '../../libraries/api';
import { PDF_SENDER_EMAIL, isPdfDownloadOperator } from '../../config';

// Shape check only; whether the address works is proven by the email arriving.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Offered instead of the preview/order flow to the operators listed in
// config/index.js. Takes an email address and queues a request for the
// photobook to be rendered and emailed as a PDF.
const PdfRequest = () => {
    const { userId, passcode } = useParams();

    const [email, setEmail] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [error, setError] = useState(null);

    // Reached by typing the URL for an operator without PDF download; they get
    // the normal flow instead.
    if (!isPdfDownloadOperator(userId)) return <Navigate to="/login" replace />;

    const canSend = EMAIL_RE.test(email.trim()) && !isSending;

    const send = async () => {
        if (!canSend) return;
        setIsSending(true);
        setError(null);
        const res = await submitPdfRequest(userId, passcode, email.trim());
        if (!res.res?.ok || res.data?.error) {
            setError(res.data?.error ?? 'There was an error sending your request. Please try again.');
            setIsSending(false);
            return;
        }
        setIsSending(false);
        setIsSent(true);
    };

    const senderNote = (
        <>
            It may take several minutes before the file is received into your email
            inbox. Please check spam folders as sometimes the PDF file can end up in
            there. The email will come from <a className="hyperlink" href={`mailto:${PDF_SENDER_EMAIL}`}>{PDF_SENDER_EMAIL}</a>
        </>
    );

    const renderForm = () => (
        <>
            <p>
                You can receive all the photos from your Photobook as a PDF file. In
                order to get this, please fill in your email address in the field
                below, and press SEND.
            </p>
            <TextInput
                className="pdf-request-input"
                type="email"
                value={email}
                placeholder="Email address"
                autoFocus={true}
                disabled={isSending}
                onChange={e => setEmail(e.target.value)}
                onKeyUp={e => {
                    if (e.code === 'Enter') send();
                }}
            />
            { error && <p className="error">{error}</p> }
            <Button onClick={send} disabled={!canSend}>
                { isSending ? 'Sending...' : 'SEND' }
            </Button>
        </>
    );

    const renderThankYou = () => (
        <p>
            Your PDF is being prepared and will be with you shortly. Note that {senderNote}
        </p>
    );

    return (
        <div className="pdf-request">
            <header>
                <img src="/images/vamoos_logo.png" alt="" />
                <h1 className="title">Photobook</h1>
            </header>
            <main>
                { isSent ? renderThankYou() : renderForm() }
            </main>
        </div>
    );
};

export default PdfRequest;
